import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import { SKIP, visit } from 'unist-util-visit';

import remarkGroupedCards from '../remarkGroupedCards';
import remarkSingleCard from '../remarkSingleCard';

import { normalizeSlackToMarkdown } from './slackish';

import type { Node as MdastNode, Root as MdastRoot } from 'mdast';

const remarkGroupedCardsPlugin = remarkGroupedCards as never;
const remarkSingleCardPlugin = remarkSingleCard as never;

type HastElement = {
  children?: unknown[];
  properties?: {
    className?: string | string[];
    [key: string]: unknown;
  };
  tagName: string;
  type: 'element';
};

type HastParent = {
  children: unknown[];
  type: string;
};

export type MarkdownClipboardPayload = {
  html: string;
  plainText: string;
};

type ErrorWithCause = Error & { cause?: unknown };

const wrapError = (message: string, err: unknown): Error => {
  if (err instanceof Error) {
    const wrapped = new Error(message);
    (wrapped as ErrorWithCause).cause = err;
    return wrapped;
  }
  return new Error(message);
};

const minimalSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: ['href'],
    td: ['align'],
    th: ['align'],
  },
  tagNames: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'ul',
    'ol',
    'li',
    'strong',
    'em',
    'a',
    'blockquote',
    'code',
    'pre',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'del',
  ],
};

const normalizeClassList = (
  properties?: HastElement['properties'],
): string[] => {
  const className = properties?.className;
  if (Array.isArray(className)) {
    return className;
  }
  if (typeof className === 'string') {
    return className.split(/\s+/u).filter(Boolean);
  }
  return [];
};

function rehypeNormalizeCards() {
  return (tree: unknown) => {
    visit(tree as never, 'element', (node: HastElement) => {
      if (node.tagName !== 'div') {
        return;
      }
      const classList = normalizeClassList(node.properties);
      if (!classList.includes('card-block')) {
        return;
      }
      Object.assign(node, {
        properties: {},
        tagName: 'blockquote',
      });
    });

    visit(
      tree as never,
      'element',
      (node: HastElement, index: number | undefined, parent: unknown) => {
        if (node.tagName !== 'div') {
          return undefined;
        }
        const classList = normalizeClassList(node.properties);
        if (!classList.includes('grouped-cards')) {
          return undefined;
        }
        if (!parent || index === undefined) {
          return undefined;
        }
        const parentNode = parent as HastParent;
        parentNode.children.splice(index, 1, ...(node.children ?? []));
        return [SKIP, index];
      },
    );
  };
}

const joinPlainTextParts = (parts: string[]): string => {
  let combined = '';

  parts.forEach((part) => {
    if (part === '\n') {
      if (combined && !combined.endsWith('\n')) {
        combined += '\n';
      }
      return;
    }

    const trimmed = part.trim();
    if (!trimmed) {
      return;
    }

    if (!combined || combined.endsWith('\n')) {
      combined += trimmed;
      return;
    }

    if (/^[.,!?;:)\]]/u.test(trimmed)) {
      combined += trimmed;
      return;
    }

    combined += ` ${trimmed}`;
  });

  return combined;
};

const nodeToPlainText = (node: MdastNode): string => {
  switch (node.type) {
    case 'text':
    case 'code':
    case 'inlineCode':
      return 'value' in node && typeof node.value === 'string'
        ? node.value
        : '';
    case 'break':
      return '\n';
    case 'list': {
      const children =
        'children' in node && Array.isArray(node.children) ? node.children : [];
      const isOrderedList = 'ordered' in node && node.ordered === true;
      const orderedStart =
        'start' in node && typeof node.start === 'number' ? node.start : 1;
      return children
        .map((child) => nodeToPlainText(child).trim())
        .filter(Boolean)
        .map((item, index) =>
          isOrderedList ? `${orderedStart + index}. ${item}` : `- ${item}`,
        )
        .join('\n');
    }
    case 'listItem': {
      const children =
        'children' in node && Array.isArray(node.children) ? node.children : [];
      const inlineParts: string[] = [];
      const nestedBlocks: string[] = [];

      children.forEach((child) => {
        const plainChild = nodeToPlainText(child).trim();
        if (!plainChild) {
          return;
        }
        if (child.type === 'list') {
          nestedBlocks.push(
            plainChild
              .split('\n')
              .map((line) => (line.trim() ? `  ${line}` : line))
              .join('\n'),
          );
          return;
        }
        inlineParts.push(plainChild);
      });

      return [joinPlainTextParts(inlineParts), ...nestedBlocks]
        .filter(Boolean)
        .join('\n');
    }
    default:
      if ('children' in node && Array.isArray(node.children)) {
        return joinPlainTextParts(
          node.children.map((child) => nodeToPlainText(child)),
        );
      }
      return '';
  }
};

const markdownToPlainText = (markdown: string): string => {
  try {
    const processor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkGroupedCardsPlugin)
      .use(remarkSingleCardPlugin);
    const tree = processor.parse(markdown);
    const transformed = processor.runSync(tree) as MdastRoot;
    return transformed.children
      .map((node) => nodeToPlainText(node).trim())
      .filter(Boolean)
      .join('\n\n');
  } catch (err) {
    throw wrapError(
      `Failed to convert markdown to plain text: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`,
      err,
    );
  }
};

const markdownToHtml = async (markdown: string): Promise<string> => {
  try {
    const processor = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkGroupedCardsPlugin)
      .use(remarkSingleCardPlugin)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeRaw)
      .use(rehypeNormalizeCards)
      .use(rehypeSanitize, minimalSchema)
      .use(rehypeStringify);
    const file = await processor.process(markdown);
    return String(file);
  } catch (err) {
    throw wrapError(
      `Failed to convert markdown to HTML: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`,
      err,
    );
  }
};

export const buildMarkdownClipboardPayload = async (
  markdown: string,
): Promise<MarkdownClipboardPayload> => {
  try {
    const normalized = normalizeSlackToMarkdown(markdown || '');
    const html = await markdownToHtml(normalized);
    const plainText = markdownToPlainText(normalized);
    return { html, plainText };
  } catch (err) {
    throw wrapError(
      `Failed to build clipboard payload: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`,
      err,
    );
  }
};

export const copyMarkdownToClipboard = async (
  markdown: string,
): Promise<void> => {
  if (!markdown.trim()) {
    return;
  }

  try {
    const { html, plainText } = await buildMarkdownClipboardPayload(markdown);
    const failures: unknown[] = [];
    const clipboard: Partial<Clipboard> | undefined =
      typeof navigator === 'undefined' ? undefined : navigator.clipboard;

    if (
      typeof clipboard?.write === 'function' &&
      typeof ClipboardItem !== 'undefined'
    ) {
      try {
        await clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
          }),
        ]);
        return;
      } catch (err) {
        failures.push(err);
      }
    }

    if (typeof clipboard?.writeText === 'function') {
      try {
        await clipboard.writeText(plainText);
        return;
      } catch (err) {
        failures.push(err);
      }
    }

    if (typeof document !== 'undefined') {
      let textarea: HTMLTextAreaElement | null = null;
      try {
        textarea = document.createElement('textarea');
        textarea.value = plainText;
        textarea.style.left = '-9999px';
        textarea.style.position = 'fixed';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand('copy');
        if (!ok) {
          throw new Error('document.execCommand("copy") returned false');
        }
        return;
      } catch (err) {
        failures.push(err);
      } finally {
        textarea?.parentNode?.removeChild(textarea);
      }
    }

    if (failures.length > 0) {
      throw failures[failures.length - 1];
    }

    throw new Error('Clipboard API unavailable');
  } catch (err) {
    throw wrapError(
      `Failed to copy to clipboard: ${
        err instanceof Error ? err.message : 'Unknown error'
      }`,
      err,
    );
  }
};
